import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Toolbar from '@mui/material/Toolbar';
import clsx from 'clsx';
import { MAIN_THEME } from '@styles';

interface IEnhancedToolbar {
  onChange: (value: string) => void;
  cdnUrl: string;
  notes?: boolean;
  title?: string;
  separator?: boolean;
  filterElem?: JSX.Element;
  classes?: { leftSection?: string; rightSection?: string };
  forceClearSearchValue?: boolean;
  children?: React.ReactNode;
  specificDesign?: boolean;
  fieldNextToTitle?: JSX.Element;
  keywordSearch?: string;
  showInlineFilter?: boolean;
}

const useStyles = makeStyles<{
  cdnUrl: string;
  notes: boolean;
  separator: boolean;
  specificDesign: boolean;
  fieldNextToTitle?: JSX.Element;
}>()(
  (theme, { cdnUrl, notes, separator, specificDesign, fieldNextToTitle }) => ({
    root: {
      padding: notes ? '0' : '1px 16px',
      minHeight: '20px!important',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      '& .MuiButton-root': {
        textTransform: specificDesign ? 'capitalize' : 'uppercase',
        color: specificDesign ? 'black' : 'white',
        fontFamily: MAIN_THEME.font.regular,
        backgroundColor: 'white',
        border: `1px solid ${MAIN_THEME.color.white86} !important`,
        padding: '0px 12px',
        margin: '8px',
        '&:hover': {
          backgroundColor: ` ${MAIN_THEME.color.white86} !important`,
        },
      },
      '& .MuiAutocomplete-hasPopupIcon.css-1h51icj-MuiAutocomplete-root .MuiAutocomplete-inputRoot, .MuiAutocomplete-hasClearIcon.css-1h51icj-MuiAutocomplete-root .MuiAutocomplete-inputRoot':
        {
          width: '50%',
        },
    },
    searchbar: {
      color: MAIN_THEME.color.black,
      height: '23px',
      minWidth: '150px',
      width: '50%',
      border: 'unset',
      outline: 'unset',
      backgroundColor: 'transparent',
      font: 'normal',
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      fontFamily: MAIN_THEME.font.regular,
      '&:focus': {
        '-webkit-input-placeholder': { color: 'transparent' },
      },
      marginRight: !separator ? 'calc(2em + 10px)' : '0',
    },
    titleWrapper: {
      marginTop: fieldNextToTitle ? '-29px' : '3px',
      marginBottom: fieldNextToTitle ? '-29px' : '3px',
      flexGrow: 1,
      flexShrink: 1,
      minWidth: '30%',
      display: 'flex',
      alignItems: fieldNextToTitle ? 'center' : '',
    },
    title: {
      flexGrow: 1,
      flexShrink: 1,
      fontFamily: MAIN_THEME.font.bold,
      fontSize: MAIN_THEME.fontSize.md,
      color: MAIN_THEME.color.black,
      alignSelf: 'center',
      width: '25px',
    },
    separator: {
      width: '1px',
      height: '25px',
      opacity: 0.5,
      backgroundColor: MAIN_THEME.color.white86,
      marginLeft: 'calc(1em + 5px)',
      marginRight: 'calc(1em + 5px)',
      display: 'inline-block',
      verticalAlign: 'text-bottom',
      alignSelf: 'center',
    },
    filterContainer: {
      backgroundSize: '16px 16px',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      paddingLeft: '40px',
      backgroundImage: `url(${cdnUrl}/icons/filter-thick-primary-new.svg)`,
      backgroundPosition: 'top 4px left 0',
      boxSizing: 'border-box',
    },
    autocompleteInput: {
      margin: '-10px 0 15px 0',
    },
  })
);

const EnhancedToolbar: React.FC<IEnhancedToolbar> = ({
  onChange,
  cdnUrl,
  classes = {},
  filterElem,
  notes = false,
  children,
  title,
  separator = false,
  forceClearSearchValue = false,
  specificDesign = false,
  fieldNextToTitle,
  keywordSearch,
  showInlineFilter = true,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const { classes: toolbarClasses } = useStyles({
    cdnUrl,
    notes,
    separator,
    specificDesign,
    fieldNextToTitle,
  });

  useEffect(() => {
    if (forceClearSearchValue) {
      setSearchValue('');
    }
  }, [forceClearSearchValue]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    onChange(event.target.value ? event.target.value.trim() : '');
  };

  const renderFilterInput = () => (
    <div
      className={toolbarClasses.filterContainer}
      style={{ width: filterElem ? 'max-content' : 'min-content' }}
    >
      {filterElem ? (
        <>
          {filterElem}
          <span className={toolbarClasses.separator} />
        </>
      ) : (
        <></>
      )}
      <input
        type="text"
        className={toolbarClasses.searchbar}
        placeholder={'Search'}
        onChange={handleChange}
        onFocus={(e) => {
          e.target.placeholder = '';
        }}
        onBlur={(e) => {
          e.target.placeholder = 'Search';
        }}
        value={searchValue || keywordSearch}
      />
    </div>
  );

  return (
    <Toolbar className={toolbarClasses.root}>
      <>
        {fieldNextToTitle ? (
          <div
            className={clsx(toolbarClasses.titleWrapper, classes.leftSection)}
          >
            {title && (
              <>
                <div className={toolbarClasses.title}>{title}</div>
                <div className={toolbarClasses.autocompleteInput}>
                  {fieldNextToTitle}
                </div>
              </>
            )}
            {showInlineFilter ? renderFilterInput() : null}
          </div>
        ) : (
          <div
            className={clsx(toolbarClasses.titleWrapper, classes.leftSection)}
          >
            {title && <div className={toolbarClasses.title}>{title}</div>}
            {showInlineFilter ? renderFilterInput() : null}
          </div>
        )}

        <div className={classes.rightSection} style={{ display: 'flex' }}>
          {separator && <span className={toolbarClasses.separator} />}
          {children}
        </div>
      </>
    </Toolbar>
  );
};

export default EnhancedToolbar;
